import { useMemo, useState, useEffect } from 'react'
import { Plus, Edit, Trash2, RefreshCw, Menu as MenuIcon, ChevronDown } from 'lucide-react'
import { useOpcoesMenu, useCreateOpcaoMenu, useUpdateOpcaoMenu, useDeleteOpcaoMenu, usePapeisComAcesso } from '../hooks/useMenu'
import { usePapeisDisponiveis } from '../../usuarios/hooks/useUsuario'
import { OpcaoMenu } from '../tipos/menu'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField } from '../../../componentesCompartilhados/form'
import { IconPicker } from '../../../componentesCompartilhados/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { usePagination } from '../../../hooks/usePagination'
import { PaginationControls } from '../../../componentesCompartilhados/paginacao/PaginationControls'
import { useToast } from '../../../contexts/NotificationContext'
import { useQueryClient } from 'react-query'
import { opcaoMenuService } from '../services/menu'
import { extractErrorMessage } from '../../../utils/formatUtils'

const menuSchema = z.object({
  titulo: z.string().min(1, 'Título é obrigatório'),
  rota: z.string(),
  icone: z.string().optional(),
  parentCodigo: z.union([z.number(), z.undefined()]).optional(),
  ordem: z.number().min(0, 'Ordem deve ser maior ou igual a zero'),
  ativo: z.boolean(),
  recurso: z.string().optional(),
})

type MenuFormData = z.infer<typeof menuSchema>

interface FormularioMenuProps {
  isOpen: boolean
  onClose: () => void
  menu?: OpcaoMenu | null
  /** Quando preenchido, o formulário é para criar um submenu deste pai (não exibe seletor de Menu Pai). */
  parentParaSubmenu?: OpcaoMenu | null
  menusDisponiveis: OpcaoMenu[]
}

// Função para normalizar texto para rota URL-friendly
function normalizarParaRota(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9]+/g, '-') // Substitui caracteres especiais por hífen
    .replace(/^-+|-+$/g, '') // Remove hífens do início e fim
}

// Função para gerar rota automaticamente (apenas para submenus; menu raiz pode ficar com rota vazia)
function gerarRotaAutomatica(titulo: string, parentCodigo: number | undefined, menusDisponiveis: OpcaoMenu[]): string {
  if (!titulo.trim()) return ''
  const rotaBase = normalizarParaRota(titulo)
  if (!parentCodigo) return '' // Menu raiz: rota vazia (será apenas agrupador)
  const menuPai = menusDisponiveis.find(m => m.codigo === parentCodigo)
  if (!menuPai) return `/${rotaBase}`
  const rotaPai = menuPai.rota.replace(/\/$/, '')
  return `${rotaPai}/${rotaBase}`
}

function FormularioMenu({ isOpen, onClose, menu, parentParaSubmenu, menusDisponiveis }: FormularioMenuProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const createMutation = useCreateOpcaoMenu()
  const updateMutation = useUpdateOpcaoMenu()
  const { data: papeisComAcesso = [], isLoading: isLoadingPapeisAcesso } = usePapeisComAcesso(menu?.codigo)
  const { data: papeisDisponiveis = [] } = usePapeisDisponiveis()
  const [papeisCodigosSelecionados, setPapeisCodigosSelecionados] = useState<number[]>([])

  const isEditing = !!menu
  const isNovoRaiz = !menu && !parentParaSubmenu
  const isNovoSubmenu = !menu && !!parentParaSubmenu

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<MenuFormData>({
    resolver: zodResolver(menuSchema),
    defaultValues: {
      titulo: '',
      rota: '',
      icone: '',
      parentCodigo: undefined,
      ordem: 0,
      ativo: true,
      recurso: '',
    },
  })

  const titulo = watch('titulo')
  const parentCodigo = watch('parentCodigo')
  const icone = watch('icone')

  const menusRaiz = useMemo(() => {
    return menusDisponiveis.filter(m => !m.parentCodigo && m.codigo !== menu?.codigo)
  }, [menusDisponiveis, menu])

  const parentCodigoEfetivo = isNovoSubmenu ? parentParaSubmenu!.codigo : isEditing ? parentCodigo : undefined

  // Gerar rota automaticamente quando título ou parent mudar (apenas para submenus)
  useEffect(() => {
    if (titulo && !isEditing) {
      const rotaGerada = gerarRotaAutomatica(titulo, parentCodigoEfetivo, menusDisponiveis)
      setValue('rota', rotaGerada)
    }
  }, [titulo, parentCodigoEfetivo, menusDisponiveis, isEditing, setValue])

  const onSubmit = async (data: MenuFormData) => {
    let rotaFinal = data.rota ?? gerarRotaAutomatica(data.titulo, parentCodigoEfetivo ?? undefined, menusDisponiveis)
    if (isNovoRaiz && data.titulo?.trim()) {
      const base = normalizarParaRota(data.titulo.trim())
      rotaFinal = base ? `/${base}` : ''
    }
    const deveAtualizarPermissoes = papeisCodigosSelecionados.length > 0 || (isEditing && menu !== undefined)

    const parentCodigoEnvio = isNovoSubmenu
      ? parentParaSubmenu!.codigo
      : (isEditing && data.parentCodigo != null && data.parentCodigo !== 0 ? data.parentCodigo : undefined)

    if (isEditing && menu) {
      await updateMutation.mutateAsync({
        codigo: menu.codigo,
        data: {
          titulo: data.titulo,
          rota: rotaFinal,
          icone: data.icone || undefined,
          parentCodigo: parentCodigoEnvio,
          ordem: data.ordem,
          ativo: data.ativo,
          recurso: data.recurso || undefined,
        },
      })
      if (deveAtualizarPermissoes) {
        opcaoMenuService
          .atualizarPermissoes(menu.codigo, papeisCodigosSelecionados)
          .then(() =>
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['opcoes-menu'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['menu'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['opcao-menu-papeis-acesso'], exact: false }),
            ])
          )
          .catch((err) => toast.error('Erro', extractErrorMessage(err, 'Erro ao aplicar permissões.')))
      }
    } else {
      const opcaoCriada = await createMutation.mutateAsync({
        titulo: data.titulo,
        rota: rotaFinal ?? '',
        icone: data.icone || undefined,
        parentCodigo: parentCodigoEnvio,
        ordem: data.ordem,
        ativo: data.ativo,
        recurso: data.recurso || undefined,
      })
      if (opcaoCriada && papeisCodigosSelecionados.length > 0) {
        opcaoMenuService
          .atualizarPermissoes(opcaoCriada.codigo, papeisCodigosSelecionados)
          .then(() =>
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['opcoes-menu'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['menu'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['opcao-menu-papeis-acesso'], exact: false }),
            ])
          )
          .catch((err) => toast.error('Erro', extractErrorMessage(err, 'Erro ao aplicar permissões.')))
      }
    }
    reset()
    onClose()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  // Reset form e papéis quando menu ou parentParaSubmenu mudam
  useEffect(() => {
    if (menu) {
      reset({
        titulo: menu.titulo,
        rota: menu.rota,
        icone: menu.icone || '',
        parentCodigo: menu.parentCodigo ?? undefined,
        ordem: menu.ordem,
        ativo: menu.ativo,
        recurso: menu.recurso || '',
      })
      setPapeisCodigosSelecionados([])
    } else {
      reset({
        titulo: '',
        rota: isNovoSubmenu ? '' : '',
        icone: '',
        parentCodigo: isNovoSubmenu ? parentParaSubmenu!.codigo : undefined,
        ordem: 0,
        ativo: true,
        recurso: '',
      })
      setPapeisCodigosSelecionados([])
    }
  }, [menu, parentParaSubmenu, isNovoSubmenu, menusDisponiveis, reset])

  // Ao carregar papeisComAcesso (edição), preencher seleção
  useEffect(() => {
    if (menu && papeisComAcesso.length >= 0 && !isLoadingPapeisAcesso) {
      setPapeisCodigosSelecionados(papeisComAcesso.map((p) => p.codigo))
    }
  }, [menu?.codigo, papeisComAcesso, isLoadingPapeisAcesso])

  const onInvalid = (errs: Partial<Record<keyof MenuFormData, { message?: string }>>) => {
    const mensagens = Object.entries(errs)
      .filter(([, v]) => v?.message)
      .map(([, v]) => v!.message!)
    toast.error('Validação', mensagens.length ? mensagens.join(' ') : 'Preencha os campos obrigatórios.')
  }

  const tituloModal = isEditing ? 'Editar Menu' : isNovoSubmenu ? 'Novo Submenu' : 'Novo Menu'

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={tituloModal}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
        <FormField label="Título" error={errors.titulo}>
          <input
            {...register('titulo')}
            placeholder="Ex: Dashboard"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
              errors.titulo ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        </FormField>

        {!isNovoRaiz && (
          <FormField label="Rota" error={errors.rota}>
            <input
              {...register('rota')}
              readOnly
              placeholder="Gerada automaticamente"
              className={`w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed ${
                errors.rota ? 'border-red-500' : 'border-gray-300'
              }`}
              title="Rota gerada automaticamente"
            />
          </FormField>
        )}

        <FormField label="Ícone" error={errors.icone}>
          <IconPicker
            value={icone}
            onChange={(iconName) => setValue('icone', iconName)}
            error={errors.icone?.message}
          />
        </FormField>

        {isNovoSubmenu && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Menu Pai:</span> {parentParaSubmenu!.titulo}
          </div>
        )}

        {isEditing && menu.parentCodigo != null && (
          <FormField label="Menu Pai" error={errors.parentCodigo}>
            <select
              {...register('parentCodigo', { valueAsNumber: true, setValueAs: (v) => (v === '' ? undefined : Number(v)) })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
                errors.parentCodigo ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Nenhum (Menu Raiz)</option>
              {menusRaiz.map((m) => (
                <option key={m.codigo} value={m.codigo}>
                  {m.titulo}
                </option>
              ))}
            </select>
          </FormField>
        )}

        <FormField label="Ordem" error={errors.ordem}>
          <input
            type="number"
            {...register('ordem', {
              valueAsNumber: true,
              setValueAs: (v) => (v === '' || Number.isNaN(Number(v)) ? 0 : Number(v)),
            })}
            placeholder="0"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
              errors.ordem ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        </FormField>

        <FormField
          label="Recurso (opcional – para permissões: quem pode ver este item)"
          error={errors.recurso}
        >
          <input
            {...register('recurso')}
            placeholder="Ex: Projetos, Pessoas, Relatórios"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
              errors.recurso ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        </FormField>

        <FormField label="Papéis com acesso">
          {isLoadingPapeisAcesso && isEditing ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500">
              Carregando...
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {papeisDisponiveis.map((papel) => {
                const codigoNum = Number(papel.codigo)
                const checked = papeisCodigosSelecionados.includes(codigoNum)
                return (
                  <label key={papel.codigo} className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        if (checked) {
                          setPapeisCodigosSelecionados((prev) => prev.filter((c) => c !== codigoNum))
                        } else {
                          setPapeisCodigosSelecionados((prev) => [...prev, codigoNum])
                        }
                      }}
                      className="h-4 w-4 text-brand-500 border-gray-300 rounded focus:ring-brand-400"
                    />
                    <span className="text-sm font-medium text-gray-900">{papel.nome}</span>
                    {papel.descricao && (
                      <span className="text-xs text-gray-500 hidden sm:inline">{papel.descricao}</span>
                    )}
                  </label>
                )
              })}
            </div>
          )}
        </FormField>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="ativo"
            {...register('ativo')}
            className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
          />
          <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
            Ativo
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 disabled:opacity-50"
          >
            {isEditing ? 'Atualizar' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function Menus() {
  const { data: opcoes = [], isLoading, refetch } = useOpcoesMenu()
  const deleteMutation = useDeleteOpcaoMenu()
  const [showForm, setShowForm] = useState(false)
  const [editingMenu, setEditingMenu] = useState<OpcaoMenu | null>(null)
  const [parentParaSubmenu, setParentParaSubmenu] = useState<OpcaoMenu | null>(null)

  const {
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    nextPage,
    previousPage,
  } = usePagination(opcoes, {
    totalItems: opcoes.length,
    itemsPerPage: 10,
  })

  const handleCreate = () => {
    setEditingMenu(null)
    setParentParaSubmenu(null)
    setShowForm(true)
  }

  const handleAddSubmenu = (menuPai: OpcaoMenu) => {
    setEditingMenu(null)
    setParentParaSubmenu(menuPai)
    setShowForm(true)
  }

  const handleEdit = (menu: OpcaoMenu) => {
    setEditingMenu(menu)
    setParentParaSubmenu(null)
    setShowForm(true)
  }

  const handleDelete = async (menu: OpcaoMenu) => {
    if (!confirm(`Deseja realmente excluir o menu "${menu.titulo}"?`)) return

    await deleteMutation.mutateAsync(menu.codigo)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingMenu(null)
    setParentParaSubmenu(null)
  }

  const menusHierarquicos = useMemo(() => {
    const buildTree = (parentCodigo?: number): OpcaoMenu[] => {
      return opcoes
        .filter(m => (m.parentCodigo ?? undefined) === (parentCodigo ?? undefined))
        .sort((a, b) => a.ordem - b.ordem)
        .map(m => ({
          ...m,
          filhos: buildTree(m.codigo),
        }))
    }
    return buildTree()
  }, [opcoes])

  const renderMenuTree = (menus: OpcaoMenu[], depth = 0): JSX.Element[] => {
    const rows: JSX.Element[] = []
    menus.forEach((menu) => {
      rows.push(
        <tr key={menu.codigo} className={depth > 0 ? 'bg-gray-50' : ''}>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center" style={{ paddingLeft: `${depth * 24}px` }}>
              {depth > 0 && <span className="mr-2 text-gray-400">└─</span>}
              <span className="text-sm font-medium text-gray-900">{menu.titulo}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{menu.rota || '-'}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{menu.icone || '-'}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{menu.ordem}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{menu.recurso || '-'}</td>
          <td className="px-6 py-4 whitespace-nowrap">
            {menu.ativo ? (
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Ativo</span>
            ) : (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Inativo</span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <div className="flex justify-end gap-2">
              {!menu.parentCodigo && (
                <button
                  onClick={() => handleAddSubmenu(menu)}
                  className="text-brand-600 hover:text-brand-900 flex items-center gap-1"
                  title="Adicionar submenu"
                >
                  <ChevronDown className="w-4 h-4" />
                  Submenu
                </button>
              )}
              <button
                onClick={() => handleEdit(menu)}
                className="text-brand-600 hover:text-brand-900"
                title="Editar"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(menu)}
                className="text-red-600 hover:text-red-900"
                title="Excluir"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
      )
      if (menu.filhos && menu.filhos.length > 0) {
        rows.push(...renderMenuTree(menu.filhos, depth + 1))
      }
    })
    return rows
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Administração de Menus</h1>
          <p className="mt-1 text-sm text-gray-600">Gerencie as opções de menu do sistema</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Novo Menu
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
              <MenuIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Opções de Menu</h3>
              <p className="text-sm text-gray-600">{opcoes.length} opção(ões) cadastrada(s)</p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
            title="Atualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando menus...</p>
          </div>
        ) : !opcoes.length ? (
          <div className="p-8 text-center">
            <MenuIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum menu encontrado</h3>
            <p className="text-gray-600">Cadastre um menu para começar.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Título
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rota
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ícone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recurso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {renderMenuTree(menusHierarquicos)}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={opcoes.length}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onNextPage={nextPage}
                  onPreviousPage={previousPage}
                  itemLabel="opção"
                />
              </div>
            )}
          </>
        )}
      </div>

      <FormularioMenu
        isOpen={showForm}
        onClose={handleCloseForm}
        menu={editingMenu}
        parentParaSubmenu={parentParaSubmenu}
        menusDisponiveis={opcoes}
      />
    </>
  )
}

export default Menus
